import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../api'
import Button from '../../components/Button'
import Card from '../../components/cards/Card'
import Table from '../../components/Table'
import TextInput from '../../components/TextInput'
import Skeleton from '../../components/Skeleton'
import { getNextEvent, parseEventDate } from '../../utils/eventDates'
import styles from './DashboardEventManager.module.css'

const formatDate = value => {
    const date = parseEventDate(value)
    return date ? new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(date) : '—'
}

export default function DashboardEventManager({ user }) {
    const navigate = useNavigate()
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [retry, setRetry] = useState(0)
    const [query, setQuery] = useState('')
    const [now, setNow] = useState(Date.now)
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 60000)
        return () => clearInterval(timer)
    }, [])
    useEffect(() => {
        const controller = new AbortController()
        setLoading(true)
        setError(false)
        api.get('/admin/events', { signal: controller.signal }).then(response => {
            if (!controller.signal.aborted) setEvents((Array.isArray(response) ? response : []).filter(event => String(event?.status || '').toLowerCase() === 'public'))
        }).catch(() => { if (!controller.signal.aborted) setError(true) })
          .finally(() => { if (!controller.signal.aborted) setLoading(false) })
        return () => controller.abort()
    }, [retry])
    const nextEvent = getNextEvent(events, now)
    const nextDate = parseEventDate(nextEvent?.starts_at)
    const visibleEvents = events.filter(event => (event.name || '').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))
    return <div className={styles.section}><div className={styles.inner}>
        <header className={styles.header}><p className={styles.eyebrow}>Dashboard</p><h1>Event Manager</h1><p>Ciao {user?.name}, organizza i prossimi appuntamenti e accedi alla gestione degli eventi.</p></header>
        {loading ? <Skeleton width="100%" height={210} borderRadius="12px" /> : error ? <div className={styles.error} role="alert">Impossibile caricare gli eventi.<Button appearance="subtle" onClick={() => setRetry(value => value + 1)}>Riprova</Button></div> : <section className={styles.featured} aria-label="Prossimo evento">
            {nextEvent ? <>
                {nextEvent.thumbnail && <img className={styles.cover} src={nextEvent.thumbnail} alt="" />}
                <div className={styles.featuredBody}><p className={styles.eyebrow}>Prossimo evento</p><h2>{nextEvent.name || 'Evento'}</h2>
                    <time dateTime={nextDate.toISOString()}>{new Intl.DateTimeFormat('it-IT', { dateStyle: 'full', timeStyle: 'short' }).format(nextDate)}</time>
                    <span className={styles.badge}>In programma</span>
                </div>
                <Link className={styles.eventLink} to={`/events/${nextEvent.id}/manager`}>Gestisci evento →</Link>
            </> : <div className={styles.featuredBody}><p className={styles.eyebrow}>Prossimo evento</p><h2>Nessun evento in programma</h2><p>Qui comparirà il prossimo evento pubblico con una data di inizio futura.</p></div>}
        </section>}
        <Card appearance="modern" integratedTable title="Eventi pubblici" actions={<span className={styles.count}>{loading ? 'Caricamento…' : error ? '' : `${events.length} eventi`}</span>}>
            <div className={styles.search}><TextInput appearance="modern" type="search" value={query} onKeyUp={setQuery} placeholder="Cerca un evento" /></div>
            {!loading && !error && !visibleEvents.length && <div className={styles.empty}>{query ? 'Nessun evento corrisponde alla ricerca.' : 'Non ci sono eventi pubblici disponibili.'}</div>}
            <Table integrated loading={loading} loadingN={6}
                columns={[
                    { key: 'name', label: 'Evento', type: 'string', wrap: true },
                    { key: 'starts_at', label: 'Inizio', type: 'custom' },
                    { key: 'ends_at', label: 'Fine', type: 'custom' },
                    { key: 'schedule', label: 'Programma', type: 'custom' },
                ]}
                onRowClick={row => { if (row?.id) navigate(`/events/${row.id}/manager`) }}
                elements={(error ? [] : visibleEvents).map(event => {
                    const start = parseEventDate(event.starts_at)
                    const end = parseEventDate(event.ends_at)
                    const label = !start ? 'Data da definire' : start.getTime() >= now ? 'In programma' : end && end.getTime() >= now ? 'In corso' : end ? 'Concluso' : 'Avviato'
                    return { ...event, name: event.name || '—',
                        starts_at: { value: start?.getTime() || 0, type: 'number', component: <span>{formatDate(event.starts_at)}</span> },
                        ends_at: { value: end?.getTime() || 0, type: 'number', component: <span>{formatDate(event.ends_at)}</span> },
                        schedule: { value: label, type: 'string', component: <span className={`${styles.badge} ${start && start.getTime() < now ? styles.mutedBadge : ''}`}>{label}</span> },
                    }
                })}
                defaultSort={{ key: 'starts_at', direction: 'desc' }} />
        </Card>
    </div></div>
}
